import {SC_GenericFile} from "./SC_GenericFile";
import {SC_User} from "../SC_User";
import {SC_ServiceFileType} from "../enums/SC_ServiceFileType";
import {ApiDateWrapper} from "../ApiDateWrapper";
import {SC_ApiTimezone} from "../enums/SC_ApiTimezone";
import {SC_ServiceInstance} from "../SC_ServiceInstance";

// @ts-ignore
export class SC_ServiceFile extends SC_GenericFile {
    /**
     * Input property name constants for fromAPI
     */
    static inputKeys = {
        service_id: 'service_id',
        type: 'file_type',
        upload_pending: 'upload_pending',
        reviewed: 'reviewed',
        uploaded: 'uploaded',
        author_id: 'author_id',
        sum: 'sum',
        reported: 'reported',
        reported_by: 'reported_by',
        reported_on: 'reported_on',
        approved_by: 'approved_by',
        approved_on: 'approved_on',
        createDate: 'createDate',
        lastModified: 'lastModified',
    } as const;

    service_id: number | null;
    service?: SC_ServiceInstance;
    type: SC_ServiceFileType;
    upload_pending: boolean | null;
    reviewed: boolean | null;
    uploaded: boolean | null;
    author_id: number | null;
    author: SC_User | null;
    private sum: string | null;
    reported: boolean | null;
    private reported_by: number | null;
    reported_by_user: SC_User | null;
    private reported_on: Date | null;
    private approved_by: number | null;
    approved_by_user: SC_User | null;
    private approved_on: Date | null;
    taken_date: ApiDateWrapper | null;

    constructor({
        service_id = null,
        type = new SC_ServiceFileType(),
        reviewed = null,
        uploaded = null,
        upload_pending = null,
        author_id = null,
        sum = null,
        missing = null,
        reported = null,
        reported_by = null,
        reported_on = null,
        approved_by = null,
        approved_on = null,

        lastModified = null,
        props = {},
    } = {}) {
        super({
            ...props
        });

        this.service_id = service_id;
        this.type = type;
        this.reviewed = reviewed;
        this.uploaded = uploaded;
        this.upload_pending = upload_pending;
        this.author_id = author_id;
        this.author = null;
        this.sum = sum;
        this.reported = reported;
        this.reported_by = reported_by;
        this.reported_on = reported_on;
        this.approved_by = approved_by;
        this.approved_on = approved_on;
        this.taken_date = lastModified;
    }

    static fromAPI(jsonin: Record<string, any>, userCache: SC_User[] = [], serviceInstanceCache: SC_ServiceInstance[] = []): SC_ServiceFile {
        const out = new SC_ServiceFile({
            service_id: jsonin[SC_ServiceFile.inputKeys.service_id] ?? null,
            type: SC_ServiceFileType.fromAPI(jsonin[SC_ServiceFile.inputKeys.type]),
            reviewed: jsonin[SC_ServiceFile.inputKeys.reviewed] ?? null,
            uploaded: jsonin[SC_ServiceFile.inputKeys.uploaded] ?? null,
            upload_pending: jsonin[SC_ServiceFile.inputKeys.upload_pending] ?? null,
            author_id: jsonin[SC_ServiceFile.inputKeys.author_id] ?? null,
            sum: jsonin[SC_ServiceFile.inputKeys.sum] ?? null,
            reported: jsonin[SC_ServiceFile.inputKeys.reported] ?? null,
            reported_by: jsonin[SC_ServiceFile.inputKeys.reported_by] ?? null,
            reported_on: jsonin[SC_ServiceFile.inputKeys.reported_on] ? new Date(jsonin[SC_ServiceFile.inputKeys.reported_on]) : null,
            approved_by: jsonin[SC_ServiceFile.inputKeys.approved_by] ?? null,
            approved_on: jsonin[SC_ServiceFile.inputKeys.approved_on] ? new Date(jsonin[SC_ServiceFile.inputKeys.approved_on]) : null,
            lastModified: ApiDateWrapper.fromAPI((jsonin[SC_ServiceFile.inputKeys.lastModified]) ?? (jsonin[SC_ServiceFile.inputKeys.createDate])),
            props: super.basicAPIStuff(jsonin)
        });

        if (userCache)
            out.scavengeUsers(userCache);

        if (serviceInstanceCache)
            out.scavengeService(serviceInstanceCache);

        return out;
    }

    static fromAPIArray(jsoninarr: Array<Record<string, unknown>>, userCache: SC_User[] = [], serviceInstanceCache: SC_ServiceInstance[] = []): SC_ServiceFile[] | null {
        return jsoninarr?.map(jsonin => SC_ServiceFile.fromAPI(jsonin, userCache, serviceInstanceCache)) ?? null;
    }

    toAPI(): Record<string, unknown> {
        return {
            [SC_GenericFile.inputKeys.href]: this.href,
            [SC_ServiceFile.inputKeys.reviewed]: this.reviewed,
            [SC_ServiceFile.inputKeys.sum]: this.sum,
            [SC_ServiceFile.inputKeys.reported_by]: this.reported_by,
            [SC_ServiceFile.inputKeys.reported_on]: this.reported_on ? this.reported_on.toISOString() : null,
            [SC_ServiceFile.inputKeys.approved_by]: this.approved_by,
            [SC_ServiceFile.inputKeys.approved_on]: this.approved_on ? this.approved_on.toISOString() : null,
        };
    }

    clone(): SC_ServiceFile {
        const cloned = new SC_ServiceFile();

        cloned._clone(this);

        cloned.type = this.type.clone();
        cloned.service_id = this.service_id;
        cloned.reviewed = this.reviewed;
        cloned.sum = this.sum;
        cloned.reported_by = this.reported_by;
        cloned.reported_on = this.reported_on;
        cloned.approved_by = this.approved_by;
        cloned.approved_on = this.approved_on;

        return cloned;
    }

    /**
     * Merges another SC_WorkorderReport object into this one, replacing null fields
     * @param {SC_ServiceFile} other
     * @returns {SC_ServiceFile}
     */
    merge(other: SC_ServiceFile): SC_ServiceFile {
        super.merge(other);

        this.type.merge(other.type);

        if (other.service_id !== null) this.service_id = other.service_id;
        if (other.reviewed !== null) this.reviewed = other.reviewed;
        if (other.sum !== null) this.sum = other.sum;
        if (other.reported_by !== null) this.reported_by = other.reported_by;
        if (other.reported_on !== null) this.reported_on = other.reported_on;
        if (other.approved_by !== null) this.approved_by = other.approved_by;
        if (other.approved_on !== null) this.approved_on = other.approved_on;

        return this;
    }

    scavengeUsers(users?: SC_User[]): void {
        if (users) {
            if (this.author_id) {
                let internalAuthor = users.find(user => user.insiteID === this.author_id);
                if (internalAuthor)
                    this.author = internalAuthor;
            }
            if (this.reported_by) {
                let internalAuthor = users.find(user => user.insiteID === this.reported_by);
                if (internalAuthor)
                    this.reported_by_user = internalAuthor;
            }
            if (this.approved_by) {
                let internalAuthor = users.find(user => user.insiteID === this.approved_by);
                if (internalAuthor)
                    this.approved_by_user = internalAuthor;
            }
        }
    }

    scavengeService(serviceInstanceCache?: SC_ServiceInstance[]): void {
        if (this.service_id && serviceInstanceCache) {
            let internalAuthor = serviceInstanceCache.find(user => user.internalID === this.service_id);
            if (internalAuthor)
                this.service = internalAuthor;
        }
    }

    uploadable() {
        return this.type.is(SC_ServiceFileType.QC_BEFORE) || this.type.is(SC_ServiceFileType.QC_AFTER) || this.type.is(SC_ServiceFileType.REPORT);
    }

    get uploadinged() {
        return this.uploaded || this.upload_pending;
    }

    rehydrateDates(tz: SC_ApiTimezone) {
        super.rehydrateDates(tz);

        this.taken_date = this.taken_date?.rehydrate(tz) ?? null;
    }
}
